import { NumberStatusValue, numberStatusEnum, PENDING_NUMBER_STATUSES } from './models'

/**
 * The three steps of the comms setup wizard. These are the *decisions* the admin
 * moves through, not the underlying number statuses: `provisioning` and
 * `pending_approval` both resolve to the single "getting it ready" waiting step.
 */
export const setupStepEnum = {
  GET_NUMBER: 'get_number',
  GETTING_READY: 'getting_ready',
  READY: 'ready',
} as const

export type SetupStep = (typeof setupStepEnum)[keyof typeof setupStepEnum]

/** Fixed left-to-right order of the wizard steps. */
export const SETUP_STEP_ORDER: SetupStep[] = [
  setupStepEnum.GET_NUMBER,
  setupStepEnum.GETTING_READY,
  setupStepEnum.READY,
]

/** Minimal shape the derivation needs — status decides the step; a stored
 * `failureReason` lets a released number be recognised as a failed review
 * rather than a deliberate cancellation. */
export interface SetupNumber {
  status: NumberStatusValue
  failureReason?: string | null
}

/**
 * The wizard step the org's current number state implies. Pure: the same set of
 * numbers always yields the same step, so returning to the wizard (reload, nav,
 * re-entry) always resumes at the right place.
 *
 * Precedence — an org can hold several numbers in different states, so the most
 * advanced wins:
 *   - any `active` number        → You're set (ready to message)
 *   - else any still-settling     → Getting it ready (provisioning / pending approval)
 *   - otherwise                   → Get a number (none, or only suspended/released)
 */
export function deriveSetupStep(numbers: SetupNumber[]): SetupStep {
  if (numbers.some((n) => n.status === numberStatusEnum.ACTIVE)) {
    return setupStepEnum.READY
  }
  if (numbers.some((n) => PENDING_NUMBER_STATUSES.includes(n.status))) {
    return setupStepEnum.GETTING_READY
  }
  return setupStepEnum.GET_NUMBER
}

/**
 * The number whose Meta review failed, if the org is not otherwise active or
 * waiting. Review failure releases the number and stores a reason (see the
 * backend `reject`), so a `released` number carrying a `failureReason` is a
 * genuine failure — distinct from a number the admin deliberately cancelled,
 * which is released with no reason. Returned so the wizard can surface the
 * plain-language reason and a resubmit path instead of silently dropping the
 * admin back to an empty "Get a number" step (a dead end).
 *
 * Suppressed while any number is active or still settling: that more-advanced
 * state is what the wizard should show, not a stale earlier failure.
 */
export function deriveFailedNumber(numbers: SetupNumber[]): SetupNumber | undefined {
  if (numbers.some((n) => n.status === numberStatusEnum.ACTIVE)) return undefined
  if (numbers.some((n) => PENDING_NUMBER_STATUSES.includes(n.status))) return undefined
  return numbers.find((n) => n.status === numberStatusEnum.RELEASED && Boolean(n.failureReason))
}

/**
 * Whether a given step sits before the current step — i.e. it has been passed
 * and should render as complete in the indicator. Steps ahead of current are
 * neither current nor complete (locked).
 */
export function isSetupStepComplete(step: SetupStep, currentStep: SetupStep): boolean {
  return SETUP_STEP_ORDER.indexOf(step) < SETUP_STEP_ORDER.indexOf(currentStep)
}
